/**
 * Cockpit OPERATE view (Cockpit Phase 5 — the user-facing "open + operate a
 * published cockpit" surface).
 *
 * This is the missing *window* into the cockpit runtime. The editor
 * (`ArgusLiveEditor`) mounts the iframe host in a small preview pane; the
 * OPERATE view mounts the SAME runtime host full-bleed against the dashboard's
 * CURRENT PUBLISHED version, plus a toggleable Resident side panel.
 *
 * Layout:
 *   +-------------------------------------------------+
 *   |  Header — title / status / Resident toggle / Edit |
 *   +-----------------------------+-------------------+
 *   |  Full-bleed runtime host    |   Resident panel  |
 *   |  (published version iframe) |   (chips + chat)  |
 *   +-----------------------------+-------------------+
 *
 * Permission model (two-layer, mirrors the editor):
 *   - Layer 1: `read:argus_live` — feature access (enforced by the route).
 *   - Layer 2: `effectiveRole` (per-dashboard grant):
 *       * any non-null grant  → can OPEN + operate-READ (view-only operate).
 *       * operator | edit | admin → can EXECUTE gated actions from the cockpit.
 *       * edit | admin → can also reach the EDITOR (`/edit`).
 *   The runtime host's execute-action endpoint re-enforces all of this
 *   server-side; the UI gating is a usability layer, not the security boundary.
 *
 * Approval surfacing (Cockpit Phase 3):
 *   When the user triggers an action that the server gates as
 *   `approval_required`, the execute-action dispatcher returns
 *   `{ status: 'pending_approval', approvalId }`. We wrap the default
 *   dispatcher so that outcome ALSO raises a parent-side pending-approval card
 *   (a different user can then approve via `/agent-approvals/:id/approve`). The
 *   wrapped dispatcher still returns the same result to the host, so the iframe
 *   gets its ACTION_RESULT and the in-cockpit button settles normally.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  Pencil,
  PanelRightClose,
  PanelRight,
  RefreshCw,
  ExternalLink,
  Code as CodeIcon,
  Loader2,
  X,
} from 'lucide-react';
import { useNavigate, Link } from '@tanstack/react-router';
import {
  RUNTIME_PROTOCOL_VERSION,
  type HostToIframeEvent,
} from '@argusai/ai-core/argus-live/runtime/post-message-protocol';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { StatusPill } from '@/components/ui/status-pill';
import { ApprovalCard } from '@/components/chat/approval-card';
import { useTheme } from '@/components/theme-provider';
import type { ArgusLiveDashboardWithVersion } from '@/lib/use-argus-live';
import {
  statusToneFor,
  canExecuteCockpitActions,
  canReachCockpitEditor,
} from '@/lib/argus-live-helpers';
import type { SubscribeBindingSummary } from '@/lib/use-argus-live-subscribe';
import { useArgusLiveIframeToken } from '@/lib/use-argus-live-iframe-token';

import { ArgusLivePostMessageHost } from '../post-message-host';
import { buildDefaultDispatcher } from '../use-argus-live-dashboard-host';
import {
  consoleHostLogger,
  type ArgusLiveHostDispatcher,
} from '../post-message-host-core';
import { CockpitResidentPanel } from './cockpit-resident-panel';
import type { ExportControl, Sensitivity } from '@argusai/shared';

// ─── Binding manifest → host payload helpers ────────────────────────────────
//
// These mirror `argus-live-editor.tsx`'s manifest mappers exactly. The OPERATE
// view renders the PUBLISHED version, so it reads the bindings off
// `currentVersion`.

type RuntimeReadyPayload = Omit<
  Extract<HostToIframeEvent, { type: 'READY' }>,
  'type'
>;

function runtimeBindingsFromManifest(
  manifest: unknown[],
): RuntimeReadyPayload['bindings'] {
  return manifest.flatMap((raw) => {
    if (!raw || typeof raw !== 'object') return [];
    const b = raw as Record<string, unknown>;
    const id = typeof b.id === 'string' ? b.id.trim() : '';
    const name =
      typeof b.name === 'string' && b.name.trim().length > 0
        ? b.name.trim().slice(0, 200)
        : id;
    if (!id || id.length > 120 || !name) return [];
    const hint =
      b.classificationHint &&
      typeof b.classificationHint === 'object' &&
      !Array.isArray(b.classificationHint)
        ? (b.classificationHint as {
            sensitivity?: unknown;
            exportControl?: unknown;
          })
        : {};
    const sensitivity: Sensitivity =
      hint.sensitivity === 'cui' || hint.sensitivity === 'restricted'
        ? hint.sensitivity
        : 'unclassified';
    const exportControl: ExportControl =
      hint.exportControl === 'itar' || hint.exportControl === 'ear'
        ? hint.exportControl
        : 'none';
    const refreshIntervalMs =
      typeof b.refreshIntervalMs === 'number' &&
      Number.isFinite(b.refreshIntervalMs) &&
      b.refreshIntervalMs >= 5_000 &&
      b.refreshIntervalMs <= 86_400_000
        ? Math.trunc(b.refreshIntervalMs)
        : null;
    return [
      {
        id,
        name,
        classificationHint: { sensitivity, exportControl },
        refreshIntervalMs,
      },
    ];
  });
}

function subscribeBindingsFromManifest(
  manifest: unknown[],
): SubscribeBindingSummary[] {
  return manifest.flatMap((raw) => {
    if (!raw || typeof raw !== 'object') return [];
    const b = raw as Record<string, unknown>;
    const id = typeof b.id === 'string' ? b.id.trim() : '';
    const refreshMode =
      typeof b.refreshMode === 'string' ? b.refreshMode : 'manual';
    if (!id || id.length > 120) return [];
    const tables = Array.isArray(b.tables)
      ? b.tables.filter((x): x is string => typeof x === 'string')
      : undefined;
    const params =
      b.params && typeof b.params === 'object' && !Array.isArray(b.params)
        ? (b.params as Record<string, unknown>)
        : undefined;
    return [{ id, refreshMode, tables, params }];
  });
}

// ─── Pending approval surfaced to the parent ────────────────────────────────

interface PendingCockpitApproval {
  approvalId: string;
  actionId: string;
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface CockpitOperateViewProps {
  /** Server response from `useArgusLiveDashboard(id)`. */
  dashboardData: ArgusLiveDashboardWithVersion;
  /** Layer-1 `write:argus_live` — needed (with edit grant) to reach the editor. */
  canWriteFeature: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function CockpitOperateView({
  dashboardData,
  canWriteFeature,
}: CockpitOperateViewProps) {
  const navigate = useNavigate();
  const { mode } = useTheme();
  const dashboard = dashboardData.dashboard;
  const publishedVersion = dashboardData.currentVersion;
  const effectiveRole = dashboardData.effectiveRole;

  const [residentOpen, setResidentOpen] = useState(true);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [pendingApproval, setPendingApproval] =
    useState<PendingCockpitApproval | null>(null);

  const mayExecute = canExecuteCockpitActions(effectiveRole);
  const mayEdit = canReachCockpitEditor(effectiveRole) && canWriteFeature;

  // The OPERATE view ALWAYS renders the published version (currentVersionId).
  // A draft-only dashboard has no published version yet → empty-state.
  const publishedVersionId = dashboard.currentVersionId ?? null;

  const previewBindings = useMemo(
    () =>
      Array.isArray(publishedVersion?.dataBindings)
        ? publishedVersion.dataBindings
        : [],
    [publishedVersion],
  );

  const readyPayload = useMemo<RuntimeReadyPayload>(
    () => ({
      protocolVersion: RUNTIME_PROTOCOL_VERSION,
      bindings: runtimeBindingsFromManifest(previewBindings),
      classification: {
        sensitivity:
          publishedVersion?.classificationTag.sensitivity ??
          dashboard.classificationTag.sensitivity,
        exportControl:
          publishedVersion?.classificationTag.exportControl ??
          dashboard.classificationTag.exportControl,
      },
      theme: {
        mode: mode === 'dark' ? 'dark' : 'light',
        primaryColor: '#1890ff',
        surfaceColor: mode === 'dark' ? '#020817' : '#ffffff',
      },
    }),
    [dashboard.classificationTag, mode, previewBindings, publishedVersion],
  );

  const subscribeBindings = useMemo(
    () => subscribeBindingsFromManifest(previewBindings),
    [previewBindings],
  );

  const { iframeSrc, error: iframeTokenError } = useArgusLiveIframeToken({
    dashboardId: dashboard.id,
    versionId: publishedVersionId,
    refreshNonce,
  });

  // ── Dispatcher: wrap the default so a `pending_approval` ACTION outcome also
  //    raises a parent-side approval card. We keep one stable instance for the
  //    iframe's lifetime (a re-create would tear down the host).
  //
  //  IMPORTANT: `buildDefaultDispatcher` couples its `sendToIframe` to the ref
  //  it is built with. The host (`ArgusLivePostMessageHost` →
  //  `useArgusLiveDashboardHost`) owns the ref attached to the REAL <iframe>,
  //  and we don't get a handle to it. So we override `sendToIframe` to resolve
  //  the live iframe from the DOM by its dashboard-id data attribute and post
  //  to its contentWindow directly — this is exactly what the default
  //  dispatcher does, just sourced from the mounted element instead of a ref
  //  we never attach. We only override `executeAction` for the approval lane;
  //  the read/state/audit network methods are unchanged.
  const versionIdRef = useRef<string | null>(publishedVersionId);
  versionIdRef.current = publishedVersionId;
  const dashboardId = dashboard.id;

  const dispatcher = useMemo<ArgusLiveHostDispatcher>(() => {
    // The ref handed to `buildDefaultDispatcher` is only consulted by the
    // default `sendToIframe`, which we replace below — so a never-attached
    // ref here is fine for the network methods (they don't read it).
    const unusedRef = { current: null as HTMLIFrameElement | null };
    const base = buildDefaultDispatcher(
      unusedRef,
      consoleHostLogger,
      () => versionIdRef.current,
    );

    const resolveIframeWindow = (): Window | null => {
      const el = document.querySelector<HTMLIFrameElement>(
        `iframe[data-argus-live-dashboard-id="${dashboardId}"]`,
      );
      return el?.contentWindow ?? null;
    };

    return {
      fetchData: base.fetchData.bind(base),
      stateOp: base.stateOp.bind(base),
      emitAudit: base.emitAudit.bind(base),
      async executeAction(input) {
        const result = await base.executeAction(input);
        if (result.status === 'pending_approval' && result.approvalId) {
          // Raise the parent card so a (different) user can approve. The
          // iframe still receives its ACTION_RESULT via the host relay below.
          setPendingApproval({
            approvalId: result.approvalId,
            actionId: input.actionId,
          });
        }
        return result;
      },
      sendToIframe(event) {
        const win = resolveIframeWindow();
        if (!win) {
          consoleHostLogger.warn(
            { eventType: event.type, dashboardId },
            'cockpit-operate: iframe window not ready — message dropped',
          );
          return;
        }
        // Null-origin sandbox → targetOrigin must be '*'. The browser still
        // scopes delivery to this exact contentWindow, so this is safe.
        win.postMessage(event, '*');
      },
    };
    // The dispatcher must NOT be re-created across renders (it owns the
    // iframe binding). Stable for the component's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRefresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  return (
    <div
      className="flex flex-col h-full relative"
      data-testid="cockpit-operate-view"
    >
      {/* ── Header ──────────────────────────────────────────────────────────
          Responsive: at narrow/mobile widths (360–414px) the left (title +
          status) group truncates and the non-essential controls (open-in-tab,
          panel toggle) collapse below `sm` so the status badge never overlaps
          the action icons. The right group never shrinks. Desktop is unchanged. */}
      <div className="border-b border-border/40 px-3 sm:px-4 py-2.5 flex items-center justify-between gap-2 shrink-0 bg-background">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={() => navigate({ to: '/argus-live' })}
            aria-label="Back to dashboards"
            title="Back to dashboards"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Separator
            orientation="vertical"
            className="h-5 hidden sm:block shrink-0"
          />
          <div className="flex items-center gap-2 min-w-0">
            <h1
              className="text-sm font-semibold truncate"
              title={dashboard.title}
            >
              {dashboard.title}
            </h1>
            <StatusPill
              status={statusToneFor(dashboard.status)}
              compact
              className="shrink-0"
            >
              {dashboard.status}
            </StatusPill>
            {!mayExecute && (
              <Badge
                variant="outline"
                className="text-[9px] border-amber-500/30 text-amber-700 dark:text-amber-400 shrink-0 hidden sm:inline-flex"
                title="You can view this cockpit but not run its actions."
              >
                view-only
              </Badge>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onRefresh}
            title="Refresh cockpit"
            aria-label="Refresh cockpit"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          {iframeSrc && (
            <a
              href={iframeSrc}
              target="_blank"
              rel="noreferrer noopener"
              aria-label="Open cockpit in new tab"
              title="Open cockpit in new tab"
              className="hidden sm:inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-accent text-muted-foreground hover:text-accent-foreground"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 hidden sm:inline-flex"
            onClick={() => setResidentOpen((x) => !x)}
            title={residentOpen ? 'Hide resident' : 'Show resident'}
            aria-label={residentOpen ? 'Hide resident' : 'Show resident'}
            data-testid="cockpit-resident-toggle"
          >
            {residentOpen ? (
              <PanelRightClose className="h-4 w-4" />
            ) : (
              <PanelRight className="h-4 w-4" />
            )}
          </Button>
          {mayEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                navigate({
                  to: '/argus-live/dashboards/$id/edit',
                  params: { id: dashboard.id },
                })
              }
              title="Open the editor"
              data-testid="cockpit-operate-edit-btn"
            >
              <Pencil className="h-3.5 w-3.5 mr-1.5" />
              Edit
            </Button>
          )}
        </div>
      </div>

      {/* ── Body: runtime host + resident panel ─────────────────────────── */}
      <div className="flex-1 min-h-0 flex">
        {/* Full-bleed runtime host */}
        <div className="flex-1 min-w-0 min-h-0 overflow-auto bg-muted/10">
          {iframeSrc ? (
            <ArgusLivePostMessageHost
              dashboardId={dashboard.id}
              versionId={publishedVersionId}
              title={`Argus Live cockpit — ${dashboard.title}`}
              src={iframeSrc}
              className="w-full h-full border-0"
              fillContainer
              iframeTestId="cockpit-operate-iframe"
              subscribeBindings={subscribeBindings}
              readyPayload={readyPayload}
              dispatcher={dispatcher}
            />
          ) : iframeTokenError && publishedVersionId ? (
            <div
              className="h-full flex flex-col items-center justify-center text-center p-6"
              data-testid="cockpit-operate-error"
            >
              <CodeIcon className="h-10 w-10 text-amber-500/40 mb-3" />
              <p className="text-sm font-medium text-muted-foreground">
                This cockpit couldn't be opened
              </p>
              <p className="text-xs text-muted-foreground/70 mt-1 max-w-sm">
                {iframeTokenError}
              </p>
            </div>
          ) : (
            <div
              className="h-full flex flex-col items-center justify-center text-center p-6"
              data-testid="cockpit-operate-empty"
            >
              <CodeIcon className="h-10 w-10 text-muted-foreground/30 mb-3" />
              <p className="text-sm font-medium text-muted-foreground">
                This cockpit hasn't been published yet
              </p>
              <p className="text-xs text-muted-foreground/70 mt-1 max-w-sm">
                {mayEdit
                  ? 'Open the editor to build and publish a version, then it will render here.'
                  : 'Ask the owner to publish a version of this cockpit.'}
              </p>
              {mayEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={() =>
                    navigate({
                      to: '/argus-live/dashboards/$id/edit',
                      params: { id: dashboard.id },
                    })
                  }
                >
                  <Pencil className="h-3.5 w-3.5 mr-1.5" />
                  Open editor
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Resident side panel */}
        {residentOpen && (
          <div className="w-[360px] shrink-0 min-h-0 hidden md:flex">
            <CockpitResidentPanel
              dashboardId={dashboard.id}
              sessionId={`operate-${dashboard.id}`}
            />
          </div>
        )}
      </div>

      {/* ── Inline approval card for a gated cockpit action ─────────────── */}
      {pendingApproval && (
        <div
          className="absolute bottom-4 right-4 w-[360px] max-w-[calc(100vw-2rem)] z-50 rounded-lg border bg-background shadow-lg p-3"
          data-testid="cockpit-action-approval"
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold">Action needs approval</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setPendingApproval(null)}
              aria-label="Dismiss approval card"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
          <ApprovalCard
            approvalId={pendingApproval.approvalId}
            args={{ action: pendingApproval.actionId }}
            summary={`The cockpit action "${pendingApproval.actionId}" requires approval before it runs.`}
            onDecision={() => {
              // Leave the card visible briefly so the user sees the terminal
              // state; then auto-dismiss + refresh the cockpit so an approved
              // write's effects show up.
              setRefreshNonce((n) => n + 1);
              setTimeout(() => setPendingApproval(null), 2500);
            }}
          />
        </div>
      )}

      {/* Footer */}
      <div className="border-t border-border/40 px-3 py-1.5 flex items-center justify-between shrink-0 text-[10px] text-muted-foreground bg-card">
        <span>
          {publishedVersion ? (
            <>Published v{publishedVersion.revLabel}</>
          ) : (
            <>No published version</>
          )}
          {' · '}
          <span className="font-mono">{dashboard.slug}</span>
        </span>
        <Link
          to="/argus-live"
          className="hover:text-foreground underline-offset-2 hover:underline"
        >
          All cockpits
        </Link>
      </div>
    </div>
  );
}
